/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { ReactNode, useEffect, useRef, useState } from 'react';

import {
  alpha,
  Box,
  Loader,
  Text,
  TextInput,
  TextProps,
  Tooltip,
} from '@mantine/core';
import { useDndMonitor, useDroppable } from '@dnd-kit/core';
import { Link } from 'react-router-dom';
import { useElementSize } from '@mantine/hooks';

import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import { NavigationItemProps } from './types';

import { vars } from '@shared/themes/vars';
import * as classes from '../styles/index.css';
import { palette } from '@app/shared/ui-kit/colors';
import {
  ListResourcesDocument,
  ListResourcesQuery,
  ListResourcesQueryVariables,
  Resource,
  SortDirection,
  useBulkMoveResourcesMutation,
} from '@app/types/blackroom-api';
import { getResourceFilterFromScope } from '@app/shared/helpers/api';
import NEW_RESOURCE_FRAGMENT from '../../api/newResourceFragment';
import { PAGE_SIZE } from '../../ResourceTable/types';
import { getResourcePathItemUrl } from '../utils';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function NavigationItem({
  scope = 'my',
  item,
  isCurrent = false,
  navigation = 'none',
  onNavigateToPathItem,
  disableRootNavigation = false,
  className,
  onRenameCurrentItem,
}: NavigationItemProps) {
  /* ********************************** HOOKS ********************************* */

  // Context
  const workspace = useWorkspace();

  // State
  const [overedByDraggableElement, setOveredByDraggableElement] = useState<
    null | any
  >(null);
  const [overId, setOverId] = useState<string>();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editedName, setEditedName] = useState(item.title);

  // Ref
  const { ref: itemRef } = useElementSize();
  const inputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);

  // API
  const [moveResources] = useBulkMoveResourcesMutation();

  // Memoization
  const droppableStyleProps: TextProps | undefined =
    overedByDraggableElement === 'inside'
      ? {
          bg: alpha(palette.blackroomBlue, 0.4),
          opacity: 0.4,
          c: palette.blackroomDarkGray5,
          style: { borderRadius: vars.radius.sm },
          p: 'sm',
        }
      : undefined;

  const isRootItem = item.id === 'root';
  const isRootDisabled = isRootItem && disableRootNavigation;
  const isCurrentEditable =
    isCurrent && !isRootItem && onRenameCurrentItem !== undefined;

  // Dnd
  const { setNodeRef: setDroppable } = useDroppable({
    id: `${item.id}`,
    disabled: isRootDisabled || isEditing,
  });
  useDndMonitor({
    onDragMove: (event) => {
      if (isRootDisabled) return;
      const { active } = event;

      if (active) {
        const draggableWidth = active.rect.current.translated?.width ?? 0;
        const draggableStart = active.rect.current.translated?.left ?? 0;
        const clip = draggableStart + (1 / 5) * draggableWidth;
        const over = x >= 0.95 * clip && x <= clip * 1.05;
        if (over) {
          setOverId(item.id);
          setOveredByDraggableElement('inside');
        } else {
          setOverId(undefined);
          setOveredByDraggableElement(null);
        }
      }
    },
    onDragEnd: async (event) => {
      const { active } = event;
      const resource = active.data.current?.resource as Resource | undefined;

      if (overId && resource) {
        const resourcesToMove = [resource];
        const parentId = overId === 'root' ? null : overId;
        await moveResources({
          variables: {
            resourcesToMove: [resource.id],
            to: parentId,
          },
          optimisticResponse: {
            __typename: 'Mutation',
            bulkMoveResources: resourcesToMove.map((r) => ({
              __typename: 'Resource',
              ...r,
              parentId,
              updatedAt: new Date().toDateString(),
            })),
          },
          update(cache, { data }) {
            cache.evict({
              id: cache.identify({ __typename: 'Resource', id: resource.id }),
              fieldName: 'path',
            });
            cache.gc();
            cache.modify({
              fields: {
                resources(getResourcesExistingRefs, { readField }) {
                  const existingResourceRefs =
                    getResourcesExistingRefs?.nodes ?? [];

                  const targetPathCachedQueryData =
                    cache.readQuery<ListResourcesQuery>({
                      query: ListResourcesDocument,
                      variables: {
                        filter: {
                          ...getResourceFilterFromScope(scope),
                          workspaceId,
                          parentId,
                        },
                        sorter: {
                          createdAt: SortDirection.Desc,
                        },
                        limit: PAGE_SIZE,
                        offset: 0,
                      } as ListResourcesQueryVariables,
                    });

                  const queryResourceRefs = existingResourceRefs.filter(
                    (ref: any) =>
                      !targetPathCachedQueryData?.resources.nodes?.some(
                        (n) => n.id === readField('id', ref),
                      ),
                  );

                  const resourcesToMoveRefs = resourcesToMove.map((r) => {
                    const newResourceData = r;
                    return cache.writeFragment({
                      data: newResourceData,
                      fragment: NEW_RESOURCE_FRAGMENT,
                    });
                  });

                  return {
                    ...getResourcesExistingRefs,
                    pageInfo: {
                      ...queryResourceRefs.pageInfo,
                      totalCount:
                        queryResourceRefs.length + resourcesToMove.length,
                    },
                    nodes: [
                      ...resourcesToMoveRefs,
                      ...(queryResourceRefs?.nodes ?? []),
                    ],
                  };
                },
              },
            });
          },
          refetchQueries: [
            {
              query: ListResourcesDocument,
              variables: {
                filter: {
                  ...getResourceFilterFromScope(scope),
                  workspaceId,
                  parentId,
                },
                limit: PAGE_SIZE,
                offset: 0,
              } as ListResourcesQueryVariables,
            },
          ],
        });
      }
      setOveredByDraggableElement(null);
    },
  });

  /* ***************************** LOCAL VARIABLES **************************** */
  const workspaceId = workspace.id;
  const x = itemRef.current?.offsetLeft;
  const { style: droppableStyle, ...droppableRestProps } =
    droppableStyleProps ?? {};

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  function startEditing() {
    setEditedName(item.title);
    setIsEditing(true);
  }

  function cancelEditing() {
    if (isSubmittingRef.current) return;

    setEditedName(item.title);
    setIsEditing(false);
  }

  async function submitRename() {
    if (isSubmittingRef.current) return;

    const name = editedName.trim();
    if (!name || name === item.title || !onRenameCurrentItem) {
      setEditedName(item.title);
      setIsEditing(false);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      await onRenameCurrentItem(name);
      setIsEditing(false);
    } catch {
      inputRef.current?.focus();
      inputRef.current?.select();
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  let inner: ReactNode = null;

  if (isCurrentEditable && isEditing) {
    inner = (
      <TextInput
        ref={inputRef}
        aria-label={item.title}
        classNames={{ input: classes.currentFolderInput }}
        value={editedName}
        readOnly={isSubmitting}
        rightSection={isSubmitting ? <Loader size="xs" /> : undefined}
        styles={{
          root: {
            maxWidth: '100%',
            width: `${Math.max(editedName.length, 1) + 2}ch`,
          },
        }}
        onBlur={() => void submitRename()}
        onChange={(event) => setEditedName(event.currentTarget.value)}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          event.stopPropagation();

          if (event.key === 'Enter') {
            event.preventDefault();
            void submitRename();
          }

          if (event.key === 'Escape') {
            event.preventDefault();
            cancelEditing();
          }
        }}
      />
    );
  } else {
    switch (navigation) {
      case 'page':
        if (isRootDisabled) {
          inner = (
            <Box ref={setDroppable} style={{ cursor: 'default' }}>
              {item.title}
            </Box>
          );
        } else {
          inner = (
            <Link
              ref={setDroppable}
              to={getResourcePathItemUrl(item, workspaceId, scope)}
              onClick={
                isCurrentEditable
                  ? (event) => {
                      event.preventDefault();
                      startEditing();
                    }
                  : undefined
              }
            >
              {item.title}
            </Link>
          );
        }

        break;
      case 'table':
        inner = (
          <Box
            ref={setDroppable}
            onClick={() => {
              if (isCurrentEditable) {
                startEditing();
              } else if (!isRootDisabled) {
                onNavigateToPathItem?.((item as any).id);
              }
            }}
          >
            {item.title}
          </Box>
        );
        break;
      default:
        break;
    }
  }

  const content = (
    <Text
      component="div"
      ref={itemRef}
      py="2"
      className={[classes.itemAnchor, className].filter(Boolean).join(' ')}
      c={isCurrent ? palette.blackroomBlack : palette.blackroomLightGray2}
      size="1.75rem"
      fw={700}
      lh={1.2}
      style={{
        cursor: isRootDisabled ? 'default' : undefined,
        ...droppableStyle,
      }}
      {...droppableRestProps}
    >
      {inner}
    </Text>
  );

  if (isEditing) return content;

  return (
    <Tooltip label={item.title} openDelay={500} withinPortal>
      {content}
    </Tooltip>
  );
}

export default NavigationItem;
